import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { fetchUsersSubscription } from '../../../../redux/actions/users/api';
import Price from '../../../../molecules/merchant/common/texts/Price';
import TicketCount from '../../../../molecules/merchant/common/texts/TicketCount';
import TicketExpiration from '../../../../molecules/merchant/common/texts/TicketExpiration';
import css from './MerchantProductDetailsHeader.module.scss';
import { MerchantProductImage } from '../../../../interfaces/merchants/products';
import { MerchantSetting } from '../../../../interfaces/merchants';
import { RSButton } from 'src/reservation-page/RSButton';
import { RSButtonLink } from 'src/reservation-page/RSButtonLink';
import { RSButtonAnchor } from 'src/reservation-page/RSButtonAnchor';
import { useUserMe } from 'src/hooks/api/me';
import { buildSiteUrl, buildUrl } from 'src/utils/request-url';

interface ElementProps {
  merchantSetting: MerchantSetting;
  imageUrl?: MerchantProductImage;
  opened: boolean;
  productId: string;
  name: string;
  price: number;
  scheme: number;
  ticketCount?: number;
  expiration?: number;
}

type Props = ElementProps;

const subscribedBy = async (scheme: number, productId: string) => {
  if (scheme !== 0) {
    return false;
  }
  const userSubscription = await fetchUsersSubscription(productId);
  return !!userSubscription;
};

const MerchantProductDetailsHeader: React.FC<Props> = ({
  merchantSetting,
  imageUrl,
  opened,
  productId,
  name,
  price,
  scheme,
  ticketCount,
  expiration,
}) => {
  const intl = useIntl();
  const [subscribed, setSubscribed] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const { user } = useUserMe();

  useEffect(() => {
    const load = async () => {
      const current = await subscribedBy(scheme, productId);
      setSubscribed(current);
      setLoaded(true);
    };
    load();
  }, [scheme, productId]);

  const classes = classNames(css.MerchantProductDetailsHeader);
  const isSubscription = scheme === 0;
  const isTicketBook = scheme === 1;
  const purchasePageHref = buildSiteUrl(
    `/${
      isSubscription ? 'subscriptions' : 'ticket_books'
    }/${productId}/purchase`,
  );
  const contactPageHref =
    merchantSetting.contact_url || buildSiteUrl(`/contact`);

  return (
    <div className={classes}>
      <div className={css.MerchantProductDetailsHeader__Inner}>
        <div className={css.MerchantProductDetailsHeader__TitleContainer}>
          <h1 className={css.MerchantProductDetailsHeader__Title}>{name}</h1>
          <div className={css.MerchantProductDetailsHeader__Summary}>
            {isTicketBook && (
              <div className={css.MerchantProductDetailsHeader__SummaryFirst}>
                <TicketCount count={ticketCount!} />
                <TicketExpiration
                  className={
                    css.MerchantProductDetailsHeader__TicketBookExpiration
                  }
                  expiration={expiration!}
                />
              </div>
            )}
            <Price
              className={css.MerchantProductDetailsHeader__Price}
              price={price}
              isMonthly={isSubscription}
            />
            <div className={css.MerchantProductDetailsHeader__ButtonContainer}>
              <div className="flex justify-end">
                {opened &&
                  (!user ? (
                    <div className="w-[200px]">
                      <RSButtonAnchor
                        variant="basic"
                        isBlock
                        href={buildUrl(
                          `/signin/user?return_to=${purchasePageHref}`,
                        )}
                      >
                        {intl.formatMessage({
                          id: `merchants.products.purchaseWithLogin`,
                        })}
                      </RSButtonAnchor>
                    </div>
                  ) : isSubscription && subscribed && loaded ? (
                    <div className="w-[128px]">
                      <RSButton variant="basic" isBlock disabled>
                        {intl.formatMessage({
                          id: `merchants.products.subscribed`,
                        })}
                      </RSButton>
                    </div>
                  ) : (
                    <div className="w-[128px]">
                      <RSButtonLink
                        variant="basic"
                        isBlock
                        href={purchasePageHref}
                        prefetch={false}
                      >
                        {isSubscription
                          ? loaded &&
                            intl.formatMessage({
                              id: `merchants.products.subscription`,
                            })
                          : loaded &&
                            intl.formatMessage({
                              id: 'merchants.products.purchase',
                            })}
                      </RSButtonLink>
                    </div>
                  ))}
                {!opened && (
                  <div className="w-[128px]">
                    <RSButtonAnchor
                      variant="basic"
                      isBlock
                      href={contactPageHref}
                    >
                      {intl.formatMessage({
                        id: 'merchants.products.inquiry',
                      })}
                    </RSButtonAnchor>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {imageUrl && imageUrl.medium?.url && (
        <div className={css.MerchantProductDetailsHeader__CardContainer}>
          <img
            className={css.MerchantProductDetailsHeader__Card}
            src={imageUrl.medium.url}
            alt={name}
          />
        </div>
      )}
    </div>
  );
};

export default MerchantProductDetailsHeader;
